import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt(n) { return Number(n || 0).toLocaleString(); }

export default function Reports({ setTitle }) {
  const [tab, setTab] = useState('sales');

  useEffect(() => { setTitle('Reports'); }, [setTitle]);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Reports</h1>
        <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>Print / Export</button>
      </div>

      <div className="card">
        <div className="tab-bar">
          {[['sales','Sales'], ['inventory','Inventory'], ['finance','Finance'], ['hr','HR']].map(([key, label]) => (
            <button key={key} className={`tab-btn ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {tab === 'sales'     && <SalesReport />}
        {tab === 'inventory' && <InventoryReport />}
        {tab === 'finance'   && <FinanceReport />}
        {tab === 'hr'        && <HRReport />}
      </div>
    </div>
  );
}

/* ─── Sales Report ─────────────────────────────────────────── */
function SalesReport() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/sales?from=${from}&to=${to}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading">Loading sales report…</div>;
  if (!data) return <div className="empty">Could not load report</div>;

  return (
    <div className="tab-content">
      <div className="filter-row no-print">
        <div className="form-group">
          <label>From</label>
          <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={load}>Apply</button>
      </div>

      <div className="stats-grid">
        {[
          { label: 'Total Orders', value: fmt(data.total_orders) },
          { label: 'Total Revenue', value: fmtMoney(data.total_revenue) },
          { label: 'Completed Orders', value: fmt(data.completed_orders) },
          { label: 'Pending Orders', value: fmt(data.pending_orders) },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ fontSize: 20 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        <div className="report-section">
          <h4>Monthly Revenue</h4>
          <table>
            <thead><tr><th>Month</th><th className="text-right">Orders</th><th className="text-right">Revenue</th></tr></thead>
            <tbody>
              {(data.monthly || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.month}</td>
                  <td className="text-right">{fmt(r.orders)}</td>
                  <td className="text-right">{fmtMoney(r.revenue)}</td>
                </tr>
              ))}
              {(data.monthly || []).length === 0 && <tr><td colSpan={3} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="report-section">
          <h4>Top Customers</h4>
          <table>
            <thead><tr><th>Customer</th><th className="text-right">Orders</th><th className="text-right">Total Spent</th></tr></thead>
            <tbody>
              {(data.top_customers || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.name}</td>
                  <td className="text-right">{fmt(r.orders)}</td>
                  <td className="text-right">{fmtMoney(r.total)}</td>
                </tr>
              ))}
              {(data.top_customers || []).length === 0 && <tr><td colSpan={3} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {data.by_status && (
        <div className="report-section">
          <h4>Orders by Status</h4>
          <table>
            <thead><tr><th>Status</th><th className="text-right">Count</th><th className="text-right">Value</th></tr></thead>
            <tbody>
              {Object.entries(data.by_status).map(([s, v]) => (
                <tr key={s}>
                  <td><span className={`badge badge-${s}`}>{s}</span></td>
                  <td className="text-right">{fmt(v.count)}</td>
                  <td className="text-right">{fmtMoney(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Inventory Report ─────────────────────────────────────── */
function InventoryReport() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/inventory').then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading inventory report…</div>;
  if (!data) return <div className="empty">Could not load report</div>;

  return (
    <div className="tab-content">
      <div className="stats-grid">
        {[
          { label: 'Total Products', value: fmt(data.total_products) },
          { label: 'Active Products', value: fmt(data.active_products) },
          { label: 'Out of Stock', value: fmt(data.out_of_stock) },
          { label: 'Low Stock', value: fmt(data.low_stock) },
          { label: 'Total Stock Value', value: fmtMoney(data.total_value) },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ fontSize: 18 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="report-section">
        <h4>Product Inventory</h4>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Category</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Cost Price</th>
                <th className="text-right">Retail Price</th>
                <th className="text-right">Stock Value</th>
              </tr>
            </thead>
            <tbody>
              {(data.products || []).map((p, i) => (
                <tr key={i}>
                  <td><strong>{p.name}</strong></td>
                  <td className="font-mono text-muted">{p.sku || '—'}</td>
                  <td>{p.category || '—'}</td>
                  <td className="text-right">
                    <span className={`stock-badge ${Number(p.stock) < 10 ? 'stock-low' : 'stock-ok'}`}>{p.stock} {p.unit}</span>
                  </td>
                  <td className="text-right">{fmtMoney(p.cost)}</td>
                  <td className="text-right">{fmtMoney(p.price)}</td>
                  <td className="text-right">{fmtMoney(parseFloat(p.stock || 0) * parseFloat(p.cost || 0))}</td>
                </tr>
              ))}
              {(data.products || []).length === 0 && <tr><td colSpan={7} className="empty">No products</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Finance Report ───────────────────────────────────────── */
function FinanceReport() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/finance').then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading finance report…</div>;
  if (!data) return <div className="empty">Could not load report</div>;

  return (
    <div className="tab-content">
      <div className="stats-grid">
        {[
          { label: 'Total Invoiced', value: fmtMoney(data.total_invoiced) },
          { label: 'Total Paid', value: fmtMoney(data.total_paid) },
          { label: 'Outstanding', value: fmtMoney(data.total_outstanding) },
          { label: 'Total Purchases', value: fmtMoney(data.total_purchases) },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ fontSize: 18 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        <div className="report-section">
          <h4>Invoices by Status</h4>
          <table>
            <thead><tr><th>Status</th><th className="text-right">Count</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {(data.invoice_by_status || []).map((r, i) => (
                <tr key={i}>
                  <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                  <td className="text-right">{fmt(r.count)}</td>
                  <td className="text-right">{fmtMoney(r.total)}</td>
                </tr>
              ))}
              {(data.invoice_by_status || []).length === 0 && <tr><td colSpan={3} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="report-section">
          <h4>Monthly Invoice Trends</h4>
          <table>
            <thead><tr><th>Month</th><th className="text-right">Invoices</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {(data.monthly_invoices || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.month}</td>
                  <td className="text-right">{fmt(r.count)}</td>
                  <td className="text-right">{fmtMoney(r.total)}</td>
                </tr>
              ))}
              {(data.monthly_invoices || []).length === 0 && <tr><td colSpan={3} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── HR Report ────────────────────────────────────────────── */
function HRReport() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/hr').then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading HR report…</div>;
  if (!data) return <div className="empty">Could not load report</div>;

  return (
    <div className="tab-content">
      <div className="stats-grid">
        {[
          { label: 'Total Employees', value: fmt(data.total_employees) },
          { label: 'Active Employees', value: fmt(data.active_employees) },
          { label: 'Departments', value: fmt(data.departments) },
          { label: 'Avg Salary', value: fmtMoney(data.avg_salary) },
          { label: 'Total Payroll', value: fmtMoney(data.total_payroll) },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ fontSize: 18 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="report-section">
        <h4>Headcount by Department</h4>
        <table>
          <thead>
            <tr>
              <th>Department</th>
              <th className="text-right">Employees</th>
              <th className="text-right">Avg Salary</th>
              <th className="text-right">Payroll Cost</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_department || []).map((r, i) => (
              <tr key={i}>
                <td><strong>{r.department || 'Unassigned'}</strong></td>
                <td className="text-right">{fmt(r.count)}</td>
                <td className="text-right">{fmtMoney(r.avg_salary)}</td>
                <td className="text-right">{fmtMoney(r.total_salary)}</td>
              </tr>
            ))}
            {(data.by_department || []).length === 0 && <tr><td colSpan={4} className="empty">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
